import { action } from '@ember/object';
import { scheduleOnce } from '@ember/runloop';
import Component from '@glimmer/component';

import type { ValidationError } from 'webapp-types';
import { uniq } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

// type Error = {
// 	name: string;
// 	message?: string;
// };

export interface Args {
	errors?: ValidationError[];
}

export default class FormErrorComponent extends Component<Args> {
	node: HTMLElement | null = null;

	get errors() {
		return this.args.errors || [];
	}

	get errorFields() {
		// uniques
		return uniq(this.errors.map((error) => error.name!));
	}

	get text() {
		let errors = [];
		if (this.hasWarnings && !this.hasErrors) {
			errors = this.errors.filter((error) => !!error.isWarning);
		} else {
			errors = this.errors.filter((error) => !error.isWarning);
		}

		return uniq(errors.map((error) => String(error.message || '').trim())).join('<br />');
	}

	get hasWarnings() {
		return this.errors.some((error) => !!error.isWarning);
	}

	get hasErrors() {
		return this.errors.some((error) => !error.isWarning);
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node;
		node.parentElement?.classList.add('validationForm');
		this.updateVisibility();
	}

	@action
	scheduleUpdateVisibility() {
		scheduleOnce('afterRender', this, this.updateVisibility);
	}

	@action
	updateVisibility() {
		const node = this.node;

		if (!node) {
			return;
		}

		// we set these classes dynamically and not through ember rendering
		// bcos RowError component also modifies them and we don't want to mess them up
		const errorFields = this.errorFields;
		const hasWarnings = this.hasWarnings;
		const hasErrors = this.hasErrors;

		if (errorFields.length === 0) {
			node.classList.remove('shown');
			node.classList.add('hidden');
			node.classList.remove('warning');
		} else if (errorFields.length === 1 && errorFields[0] === 'general') {
			// if only generic error, show
			node.classList.remove('hidden');
			node.classList.add('shown');

			// Apply warning class only when there are warnings but no errors
			if (hasWarnings && !hasErrors) {
				node.classList.add('warning');
			} else {
				node.classList.remove('warning');
			}
		}
	}

	<template>
		<div
			class="errorMessage"
			{{didInsert this.setupView}}
			{{didUpdate this.scheduleUpdateVisibility @errors}}
		>
			<CommonUiIconComponent @name="AlertFill" @size="sm" />
			<span class="message">{{{this.text}}}</span>
		</div>
	</template>
}
